import {
  MagnifyingGlassIcon,
  ChevronUpDownIcon,
} from "@heroicons/react/24/outline";
import { MdSearch, MdSearchOff } from "react-icons/md";
import React, { useEffect, useState } from "react";
import {
  userurl1,
  fetchScanData,
  getIntegrationDataWithScanStatus,
} from "../../helperFunctions/apiFunction";
import { ArrowDownIcon } from "@heroicons/react/24/solid";
import toast from "react-hot-toast";
import {
  Card,
  CardHeader,
  Input,
  Typography,
  Button,
  CardBody,
  Chip,
  CardFooter,
  IconButton,
} from "@material-tailwind/react";
import { useAuthUserToken } from "../../hooks/useAuthUserToken";
import TableSkeleton from "../../components/Loaders/TableSkeleton";

const TABLE_HEAD = [
  "Cloud",
  "Title",
  "Description",
  "Configuration",
  "Status",
  "Action",
];

export default function CreateNewScan() {
  const { refreshToken } = useAuthUserToken();
  const [templateData, setTemplateData] = useState([]);
  const [loadingStates, setLoadingStates] = useState({});
  const [isLoading, setIsLoading] = useState(null);
  const [searchBy, setSearchBy] = useState("");
  const [page, setPage] = useState(1);
  const [totalPage, setTotalPage] = useState(1);

  useEffect(() => {
    getIntegrationDataWithScanStatus(
      refreshToken,
      searchBy,
      "asc",
      100,
      1,
      ""
    ).then((data) => {
      setTotalPage(data?.data?.totalPages);
      setTemplateData(data?.data?.results);
      setIsLoading(false);
    });
  }, [searchBy]);

  const scanIcon = (row) => {
    let url = "";
    setLoadingStates((prevStates) => ({
      ...prevStates,
      [row._id]: true, // Set loading state for the clicked row
    }));
    if (row.integrationProvider === "Azure") {
      url = `${userurl1}scan/scanData?subscriptionId=${row.integrationConfig[0].subscriptionId}&clientId=${row.clientId}&cloud=${row.integrationProvider}`;
    } else if (row.integrationProvider === "AWS") {
      url = `${userurl1}scan/scanData?accountId=${row.integrationConfig[0].accountId}&clientId=${row.clientId}&cloud=${row.integrationProvider}`;
    } else if (row.integrationProvider === "GCP") {
      url = `${userurl1}scan/scanData?project=${row.integrationConfig[0].project}&clientId=${row.clientId}&cloud=${row.integrationProvider}`;
    }

    if (url) {
      fetchScanData(url, refreshToken)
        .then((response) => {
          if (response?.code === 200 || response?.code === 201) {
            toast.success("Scan initiated successfully", {
              className: "text-[12px]",
            });
          } else if (response.code === 400) {
            toast.error(response?.message, {
              className: "text-[12px]",
            });
          } else {
            toast.error(response?.message, {
              className: "text-[12px]",
            });
          }
        })
        .catch((error) => {
          toast.error("Something went wrong!", {
            className: "text-[12px]",
          });
          console.error("Error during action:", error);
        })
        .finally(() => {
          setLoadingStates((prevStates) => ({
            ...prevStates,
            [row._id]: false,
          }));
        });
    }
  };

  return (
    <div className="app-main__inner !pl-20 ">
      <Card
        className="h-full w-full rounded-3xl shadow-xl"
        style={{ backgroundImage: `url('/assets/images/beams.jpg')` }}
      >
        <CardHeader
          floated={false}
          shadow={false}
          className=" bg-transparent rounded-none !overflow-visible"
        >
          <div className="mb-8 flex flex-col md:flex-row md:items-center md:justify-between md:gap-8">
            <div>
              <Typography variant="h5" color="blue-gray">
                List of Integrations
              </Typography>
              <Typography color="gray" className="mt-1 font-normal">
                View all monitored cloud accounts
              </Typography>
            </div>
            <div className="flex flex-row md:flex-row items-center justify-between md:items-center gap-2 md:gap-8 mt-4 md:mt-0">
              <Button variant="outlined" size="sm">
                <ArrowDownIcon strokeWidth={2} className="h-4 w-4" />
              </Button>
            </div>
          </div>
          <div className="flex flex-col items-center justify-between gap-6 md:flex-row">
            <div className="w-full md:w-auto">
              <Input
                label="Search"
                className="focus:!border-black focus:!border-2 focus:!border-t-0"
                value={searchBy}
                onChange={(e) => setSearchBy(e.target.value)}
                icon={<MagnifyingGlassIcon className="h-5 w-5" />}
              />
            </div>
          </div>
        </CardHeader>
        <CardBody className="overflow-scroll px-3 p-0">
          <table className="mt-4 w-full min-w-max table-auto text-left">
            <thead>
              <tr>
                {TABLE_HEAD.map((head, index) => (
                  <th
                    key={head}
                    className="cursor-pointer border-y border-blue-gray-100 bg-blue-gray-50/50 p-4 transition-colors hover:bg-blue-gray-50"
                  >
                    <Typography
                      variant="small"
                      color="blue-gray"
                      className="flex items-center justify-between gap-2 font-normal leading-none opacity-70"
                    >
                      {head}{" "}
                      {index !== TABLE_HEAD.length - 1 && (
                        <ChevronUpDownIcon
                          strokeWidth={2}
                          className="h-4 w-4"
                        />
                      )}
                    </Typography>
                  </th>
                ))}
              </tr>
            </thead>
            {isLoading === null ? (
              <>
                <tbody>
                  <tr>
                    <td colSpan={TABLE_HEAD.length}>
                      <TableSkeleton />
                    </td>
                  </tr>
                </tbody>
              </>
            ) : (
              <>
                <tbody>
                  {isLoading === false && templateData?.length > 0 ? (
                    templateData.map(
                      (
                        {
                          integrationProvider,
                          integrationTitle,
                          integrationDescription,
                          integrationConfig,
                          integrationStatus,
                          ongoingScan,
                          clientId,
                          _id,
                        },
                        index
                      ) => {
                        const isLast = index === templateData.length - 1;
                        const classes = isLast
                          ? "p-4"
                          : "p-4 border-b border-blue-gray-50";

                        return (
                          <tr key={`${integrationTitle}-${index}`}>
                            <td className={classes}>
                              <div className="flex items-center gap-3">
                                <Typography
                                  variant="small"
                                  color="blue-gray"
                                  className="font-normal"
                                >
                                  {integrationProvider}{" "}
                                </Typography>
                              </div>
                            </td>
                            <td className={classes}>
                              <Typography
                                variant="small"
                                color="blue-gray"
                                className="font-normal"
                              >
                                {integrationTitle}
                              </Typography>
                            </td>
                            <td className={classes}>
                              <Typography
                                variant="small"
                                color="blue-gray"
                                className="font-normal"
                              >
                                {integrationDescription}
                              </Typography>
                            </td>
                            <td className={classes}>
                              <div className="flex flex-col">
                                <Typography
                                  variant="small"
                                  color="blue-gray"
                                  className="font-bold"
                                >
                                  {integrationConfig[0].accessKeyId}
                                </Typography>
                                <Typography
                                  variant="small"
                                  color="blue-gray"
                                  className="text-[14px] font-medium"
                                >
                                  {integrationConfig[0].accountId}
                                </Typography>

                                <Typography
                                  variant="small"
                                  color="blue-gray"
                                  className="text-[12px]"
                                >
                                  {integrationConfig[0].defaultRegion
                                    ? integrationConfig[0].defaultRegion
                                    : "No Data Found"}
                                </Typography>
                              </div>
                            </td>
                            <td className={classes}>
                              <div className="w-max">
                                <Chip
                                  variant="ghost"
                                  size="sm"
                                  value={
                                    integrationStatus === "Active"
                                      ? "Active"
                                      : "Deactive"
                                  }
                                  color={
                                    integrationStatus === "Active"
                                      ? "green"
                                      : "red"
                                  }
                                />
                              </div>
                            </td>

                            <td className={classes}>
                              {!loadingStates[_id] ? (
                                ongoingScan === false ? (
                                  <abbr title="Scan">
                                    <MdSearch
                                      onClick={() =>
                                        scanIcon({
                                          integrationProvider,
                                          integrationTitle,
                                          integrationDescription,
                                          integrationConfig,
                                          integrationStatus,
                                          clientId,
                                          _id,
                                        })
                                      }
                                      style={{
                                        color: "green",
                                        fontSize: "24px",
                                        display: "block",
                                        margin: "0 auto",
                                        cursor: "pointer",
                                      }}
                                    />
                                  </abbr>
                                ) : (
                                  <abbr title="Scan already In-Progress">
                                    <MdSearchOff
                                      style={{
                                        color: "red",
                                        fontSize: "24px",
                                        display: "block",
                                        margin: "0 auto",
                                        cursor: "pointer",
                                      }}
                                    />
                                  </abbr>
                                )
                              ) : (
                                <Button variant="outlined" size="sm">
                                  <div role="status">
                                    <svg
                                      aria-hidden="true"
                                      className="inline w-4 h-4 text-gray-200 animate-spin dark:text-gray-600 fill-gray-600 dark:fill-gray-300"
                                      viewBox="0 0 100 101"
                                      fill="none"
                                      xmlns="http://www.w3.org/2000/svg"
                                    >
                                      <path
                                        d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
                                        fill="currentColor"
                                      />
                                      <path
                                        d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
                                        fill="currentFill"
                                      />
                                    </svg>
                                    <span className="sr-only">Loading...</span>
                                  </div>
                                </Button>
                              )}
                            </td>
                          </tr>
                        );
                      }
                    )
                  ) : (
                    <tr>
                      <td colSpan="8">
                        <div className="flex flex-col items-center w-full">
                          <img
                            quality={100}
                            src="/assets/images/9276430.jpg"
                            className="w-[350px] mix-blend-darken"
                            alt="no-data-found"
                          />
                          <p className="text-[20px] font-medium">
                            No Data Found!
                          </p>
                        </div>
                      </td>
                    </tr>
                  )}
                </tbody>
              </>
            )}
          </table>
        </CardBody>
        {isLoading === null ? null : (
          <>
            <CardFooter className="flex items-center justify-between border-t border-blue-gray-50 p-4">
              <Button
                variant="outlined"
                size="sm"
                onClick={() => {
                  setIsLoading(null);
                  setPage(page - 1);
                }}
                disabled={totalPage > 1 ? false : true}
              >
                Previous
              </Button>
              <div className="flex items-center gap-2">
                {Array.from({ length: totalPage }, (_, index) => (
                  <IconButton
                    key={index + 1}
                    variant={index + 1 === page ? "outlined" : "text"}
                    size="sm"
                    onClick={() => {
                      setIsLoading(null);
                      setPage(index + 1);
                    }}
                    disabled={totalPage > 1 ? false : true}
                  >
                    {index + 1}
                  </IconButton>
                ))
                  .reduce((acc, button, index, array) => {
                    if (
                      index < 3 ||
                      index >= totalPage - 3 ||
                      (index >= Math.max(page - 1, 3) &&
                        index <= Math.min(page + 1, totalPage - 4))
                    ) {
                      acc.push(button);
                    } else if (acc[acc.length - 1] !== "ellipsis") {
                      acc.push("ellipsis");
                    }
                    return acc;
                  }, [])
                  .map((element, index, array) => {
                    return element === "ellipsis" ? (
                      <IconButton
                        key={`ellipsis-${index}`}
                        variant="text"
                        size="sm"
                        disabled
                      >
                        ...
                      </IconButton>
                    ) : index > 0 && array[index - 1] === "ellipsis" ? (
                      <React.Fragment key={`fragment-${index}`}>
                        {element}
                      </React.Fragment>
                    ) : (
                      element
                    );
                  })}
              </div>

              <Button
                variant="outlined"
                size="sm"
                onClick={() => {
                  setPage(page + 1);
                  setIsLoading(null);
                }}
                disabled={totalPage > 1 ? false : true}
              >
                Next
              </Button>
            </CardFooter>
          </>
        )}
      </Card>
    </div>
  );
}
